import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Stack } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchSymbolPatternIfNeeded } from '../../actions/symbolSearch';
import { removeJpFromSymbols } from '../../utils/common';
import useActions from '../../utils/hooks/useActions';
import useLocalize from '../../utils/hooks/useLocalize';
import useNewsArticleModal from '../../utils/hooks/useNewsArticleModal';
import { newsProvider } from '../../utils/newsProvider';
import DateTime from '../common/DateTime';
import NewsArticleModal from '../common/NewsArticleModal';

// Helper (not a React Hook) to parse and clean related symbols.
// Named without the `use` prefix to avoid React Hook lint rules.
const parseRelatedSymbols = (relatedSymbols) => {
  return removeJpFromSymbols(JSON.parse(relatedSymbols || '[]'));
};

const useStyles = makeStyles({
  headline: {
    background: '#ffffff',
    borderBottom: '1px solid #e2e2e2',
    borderRadius: '2px',
    paddingLeft: '15px',
    paddingRight: '15px',
    overflow: 'hidden',
    position: 'relative',
    paddingTop: '15px',
    paddingBottom: '10px',
    transition: 'max-height 200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
  },
  thinHeadline: {
    background: '#ffffff',
    borderBottom: '1px solid #e2e2e2',
    borderRadius: '2px',
    paddingLeft: '15px',
    paddingRight: '15px',
    overflow: 'hidden',
    position: 'relative',
    paddingTop: '15px',
    paddingBottom: '10px',
    transition: 'max-height 200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    maxHeight: 'calc(1rem + 15px)'
  },
  iconStyle: {
    color: '#BCBCBC',
    display: 'inlineBlock',
    marginRight: '0',
    fontSize: '9px',
    fontWeight: 'bold',
    height: '100'
  },
  headlineItem: {
    padding: '5px',
    border: '0px solid #d3d9e0',
    borderTop: '1px solid #d3d9e0'
  },
  headLineItemFirst: {
    border: '0px',
    padding: '5px'
  },
  headlineColumn: {
    width: '100px',
    whiteSpace: 'nowrap'
  },
  relatedSymbolColumn: {
    display: 'flex',
    justifyContent: 'center',
    width: '65px',
    whiteSpace: 'nowrap'
  },
  headlineHeaderColumn: {
    width: '100%'
  },
  relatedSymbolPlusIcon: {
    lineHeight: '15px',
    position: 'absolute',
    right: '-9px'
  },
  headlineLink: {
    display: 'flex',
    flexDirection: 'row',
    maxHeight: 'inherit',
    position: 'relative'
  },
  headlineLinkLeft: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column'
  },
  headlineLinkLeftTitle: {
    flexGrow: 1,
    lineHeight: '1.8em'
  },
  headlineLinkLeftProvider: {
    alignSelf: 'flex-end',
    WebkitTextSizeAdjust: '100%',
    msTextSizeAdjust: '90%',
    fontSize: '12px',
    borderTopLeftRadius: '2px',
    borderTopRightRadius: '2px',
    color: 'rgba(0, 0, 0, 0.6)',
    lineHeight: '1.2rem',
    marginLeft: '-8px',
    padding: '4px 4px 4px 12px'
  },
  headlineLinkRight: {
    display: 'flex',
    alignItems: 'center'
  },
  viewed: {
    color: '#800080 !important'
  },
  odd: {
    background: '#f7f8fa'
  },
  even: {
    background: 'white'
  },
  relatedSymbols: {
    fontSize: '0.9rem',
    wordWrap: 'break-word'
  }
});

export default function Headline(props) {
  const classes = useStyles();
  const localize = useLocalize();
  const index = props.index || 0;
  const isFirst = index === 0;
  const isOdd = index % 2;

  // Custom hook for modal management
  const {
    open,
    selectedArticle,
    openArticle,
    closeModal
  } = useNewsArticleModal();

  // Actions
  const [getSymbol] = useActions([fetchSymbolPatternIfNeeded], []);

  const selectedSymbol = props.selectedSymbol || '';
  const symbols = useSelector((state) => state.symbols || {});
  const searchResults = useMemo(() => symbols.searches || {}, [symbols]);
  const searchResultSymbols = useMemo(() => {
    const result = searchResults[selectedSymbol] || {};
    return result.symbols || [];
  }, [searchResults, selectedSymbol]);

  useEffect(() => {
    if (selectedSymbol && searchResultSymbols.length > 0) {
      window.location.hash = `/symbol/${searchResultSymbols[0]}`;
      props.setSelectedSymbol(null);
    }
  }, [props, selectedSymbol, searchResultSymbols]);

  // Handle article click - use the custom hook but also call the legacy prop
  const handleClickOpen = (article) => {
    openArticle(article);
    // Keep backward compatibility with existing prop
    if (props.addArticlesToLocalStorage) {
      props.addArticlesToLocalStorage(article.articleId);
    }
  };

  // Memoize parsing of related symbols to avoid JSON.parse on every render
  const relatedSymbols = useMemo(
    () => parseRelatedSymbols(props.article.relatedSymbols),
    [props.article.relatedSymbols]
  );

  const headLineClass = isFirst ? classes.headLineItemFirst : classes.headlineItem;
  const shadedClass = isOdd ? classes.odd : classes.even;
  const headlineClasses = `${headLineClass} ${shadedClass}`;
  const hover = relatedSymbols.length > 1 ? relatedSymbols.join(', ') : '';

  const thinHeadline = (
    <div className={headlineClasses}>
      <Stack
        direction={'row'}
        alignItems={'flex-start'}
        justifyContent={'flex-start'}
        spacing={2}
      >
        {!props.isDisableRelatedSymbols && (
          <div
            className={classes.relatedSymbolColumn}
            onClick={() => {
              props.onRelatedSymbolPress(relatedSymbols[0], getSymbol);
            }}
          >
            <Link
              to={'#'}
              id={'headline-link'}
              className={classes.headlineLink}
              title={hover}
            >
              {relatedSymbols.length > 0 && relatedSymbols[0]}
              {relatedSymbols.length >= 2 && (
                <div
                  id={'related-symbol-plus-icon'}
                  className={classes.relatedSymbolPlusIcon}
                >
                  +
                </div>
              )}
            </Link>
          </div>
        )}
        <div className={classes.headlineColumn}>
          <DateTime value={props.article.timestamp} hasZeroPad={true} />
        </div>
        <div className={classes.headlineColumn}>
          {localize(newsProvider(props.article.provider))}
        </div>
        <div className={classes.headlineHeaderColumn}>
          <Link
            id={'headline-link'}
            to={'#'}
            onClick={() => handleClickOpen(props.article)}
            className={[
              classes.headlineLink,
              props.isRead ? classes.viewed : ''
            ].join(' ')}
          >
            {props.article.title}
          </Link>
        </div>
      </Stack>
      <NewsArticleModal
        open={open}
        onClose={closeModal}
        article={selectedArticle}
        isWide={props.isWide}
      />
    </div>
  );

  const headline = (
    <div className={classes.headline} id={props.article.articleId}>
      <Link
        id={'headline-link'}
        to={'#'}
        onClick={() => handleClickOpen(props.article)}
        className={[
          classes.headlineLink,
          props.isRead ? classes.viewed : ''
        ].join(' ')}
      >
        <div id={'headline-link-left'} className={classes.headlineLinkLeft}>
          <div
            id={'headline-link-left-title'}
            className={classes.headlineLinkLeftTitle}
          >
            {props.article.title}
          </div>
          <span
            id={'headline-link-left-provider'}
            className={classes.headlineLinkLeftProvider}
          >
            <DateTime value={props.article.timestamp} hasZeroPad={true} />
            &nbsp; {localize(newsProvider(props.article.provider))}
          </span>
        </div>
        <div id={'headline-link-right'} className={classes.headlineLinkRight}>
          <ArrowForwardIosIcon
            id={'headline-link-right-icon'}
            className={classes.iconStyle}
            aria-label="view"
          />
        </div>
      </Link>
      <NewsArticleModal
        open={open}
        onClose={closeModal}
        article={selectedArticle}
        isWide={props.isWide}
      />
    </div>
  );

  return props.isWide ? thinHeadline : headline;
}